import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Scheduler, View } from 'devextreme-react/scheduler';
import AntTime from '../utils/AntTime';
import AntLocalization from '../utils/AntLocalization';

// ─── Status config ──────────────────────────────────────────────────────────
const STATUS_CONFIG = [
    { key: 'New', vi: 'Mới tạo', en: 'New', color: '#3B82F6', bg: '#EFF6FF', border: '#BFDBFE' },
    { key: 'Release', vi: 'Xác nhận', en: 'Confirmed', color: '#8B5CF6', bg: '#F5F3FF', border: '#DDD6FE' },
    { key: 'Open', vi: 'Chờ khách', en: 'Waiting', color: '#F59E0B', bg: '#FFFBEB', border: '#FDE68A' },
    { key: 'Close', vi: 'Đã đến', en: 'Arrived', color: '#10B981', bg: '#ECFDF5', border: '#A7F3D0' },
    { key: 'Cancel', vi: 'Đã hủy', en: 'Cancelled', color: '#9CA3AF', bg: '#F9FAFB', border: '#E5E7EB' }
];
const STATUS_MAP = Object.fromEntries(STATUS_CONFIG.map((s) => [s.key, s]));
function getStatus(key) { return STATUS_MAP[key] || STATUS_CONFIG[0]; }

function fmtTime(timeUnix) {
    if (!timeUnix) return '';
    try { return AntTime.new(timeUnix).format('HH:mm'); } catch (e) { return ''; }
}

// ─── AppointmentCard ────────────────────────────────────────────────────────
function AppointmentCard({ item, onClick }) {
    const sc = getStatus(item.appointment_status);
    return (
        <div
            onClick={() => onClick && onClick(item)}
            style={{
                background: '#fff',
                border: `1px solid ${sc.border}`,
                borderLeft: `4px solid ${sc.color}`,
                borderRadius: 8,
                padding: '10px 12px',
                marginBottom: 8,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                userSelect: 'none',
                minWidth: 0
            }}
        >
            {/* Row 1: time + status tag */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 6 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
                    {item.appointment_hour ? (
                        <span style={{ fontSize: 13, fontWeight: 700, color: sc.color, flexShrink: 0 }}>
                            {fmtTime(item.appointment_hour)}
                        </span>
                    ) : null}
                    {item.queue_code ? (
                        <span style={{ fontSize: 11, color: '#9CA3AF', background: '#F3F4F6', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>
                            #{item.queue_code}
                        </span>
                    ) : null}
                </div>
                <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                    color: sc.color, background: sc.bg, border: `1px solid ${sc.border}`,
                    borderRadius: 10, padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0
                }}>
                    {AntLocalization.translateText({ vi: sc.vi, en: sc.en })}
                </span>
            </div>
            {/* Row 2: customer name */}
            <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.entity_name || '—'}
            </div>
            {/* Row 3: phone */}
            {item.entity_contact_phone ? (
                <div style={{ fontSize: 12, color: '#6B7280' }}>☎ {item.entity_contact_phone}</div>
            ) : null}
            {/* Row 4: staff + place */}
            {(item.serve_entity_name || item.serve_place_name) ? (
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.serve_entity_name ? `◉ ${item.serve_entity_name}` : ''}
                    {item.serve_entity_name && item.serve_place_name ? ' · ' : ''}
                    {item.serve_place_name ? item.serve_place_name : ''}
                </div>
            ) : null}
            {/* Row 5: items description */}
            {item.items_desc ? (
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.items_desc}
                </div>
            ) : null}
            {/* Row 6: store */}
            {item.store_name ? (
                <div style={{ fontSize: 11, color: '#D1D5DB', marginTop: 3 }}>📍 {item.store_name}</div>
            ) : null}
        </div>
    );
}

// ─── KanbanView ─────────────────────────────────────────────────────────────
function KanbanView({ appointments, onCardClick, filterDate, onFilterDateChange }) {
    // Filter by selected date (if set)
    const filtered = useMemo(() => {
        if (!filterDate) return appointments;
        return appointments.filter((a) => {
            if (!a.appointment_date) return false;
            try { return AntTime.new(a.appointment_date).format('YYYY-MM-DD') === filterDate; } catch (e) { return false; }
        });
    }, [appointments, filterDate]);

    const grouped = useMemo(() => {
        const map = {};
        STATUS_CONFIG.forEach((s) => { map[s.key] = []; });
        filtered.forEach((a) => {
            const key = a.appointment_status;
            if (map[key]) map[key].push(a);
        });
        return map;
    }, [filtered]);

    const prevDay = useCallback(() => {
        if (!onFilterDateChange) return;
        const d = AntTime.new(filterDate || AntTime.now().format('YYYY-MM-DD')).subtract(1, 'day').format('YYYY-MM-DD');
        onFilterDateChange(d);
    }, [filterDate, onFilterDateChange]);

    const nextDay = useCallback(() => {
        if (!onFilterDateChange) return;
        const d = AntTime.new(filterDate || AntTime.now().format('YYYY-MM-DD')).add(1, 'day').format('YYYY-MM-DD');
        onFilterDateChange(d);
    }, [filterDate, onFilterDateChange]);

    const goToday = useCallback(() => {
        if (onFilterDateChange) onFilterDateChange(AntTime.now().format('YYYY-MM-DD'));
    }, [onFilterDateChange]);

    const isToday = filterDate === AntTime.now().format('YYYY-MM-DD');
    const dateLabel = filterDate ? AntTime.new(filterDate).format('dddd, DD/MM/YYYY') : '';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Date navigation */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px 6px', flexShrink: 0 }}>
                <button onClick={prevDay} style={navBtnStyle}>‹</button>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', minWidth: 180, textAlign: 'center' }}>
                    {dateLabel}
                    {isToday && <span style={{ marginLeft: 6, fontSize: 11, background: '#3B82F6', color: '#fff', borderRadius: 8, padding: '1px 6px' }}>Hôm nay</span>}
                </span>
                <button onClick={nextDay} style={navBtnStyle}>›</button>
                {!isToday && (
                    <button onClick={goToday} style={{ ...navBtnStyle, padding: '4px 10px', fontSize: 12 }}>Hôm nay</button>
                )}
                <span style={{ marginLeft: 6, fontSize: 12, color: '#9CA3AF' }}>{filtered.length} lịch hẹn</span>
            </div>
            {/* Columns */}
            <div style={{ flex: 1, display: 'flex', gap: 10, overflowX: 'auto', overflowY: 'hidden', padding: '4px 16px 16px' }}>
                {STATUS_CONFIG.map((sc) => {
                    const items = (grouped[sc.key] || []).sort((a, b) => (a.appointment_hour || 0) - (b.appointment_hour || 0));
                    return (
                        <div key={sc.key} style={{ flex: '0 0 264px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            {/* Column header */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '8px 10px', background: sc.bg,
                                borderRadius: '8px 8px 0 0', borderBottom: `3px solid ${sc.color}`,
                                marginBottom: 8, flexShrink: 0
                            }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: sc.color, flexShrink: 0 }} />
                                <span style={{ fontWeight: 600, fontSize: 13, color: '#374151', flex: 1 }}>
                                    {AntLocalization.translateText({ vi: sc.vi, en: sc.en })}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: sc.color, borderRadius: 10, padding: '1px 8px' }}>
                                    {items.length}
                                </span>
                            </div>
                            {/* Cards */}
                            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 2 }}>
                                {items.map((item) => (
                                    <AppointmentCard key={item.id || item.code} item={item} onClick={onCardClick} />
                                ))}
                                {items.length === 0 && (
                                    <div style={{ textAlign: 'center', color: '#E5E7EB', fontSize: 13, paddingTop: 20 }}>Trống</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─── ListView ────────────────────────────────────────────────────────────────
function ListView({ appointments, onCardClick }) {
    const grouped = useMemo(() => {
        const map = {};
        appointments.forEach((a) => {
            let key = 'unknown';
            try { key = a.appointment_date ? AntTime.new(a.appointment_date).format('YYYY-MM-DD') : 'unknown'; } catch (e) { /* skip */ }
            if (!map[key]) map[key] = [];
            map[key].push(a);
        });
        return Object.entries(map)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([dateKey, items]) => ({
                dateKey,
                dateLabel: dateKey !== 'unknown' ? AntTime.new(dateKey).format('dddd, DD/MM/YYYY') : 'Không rõ ngày',
                isToday: dateKey === AntTime.now().format('YYYY-MM-DD'),
                items: items.sort((a, b) => (a.appointment_hour || 0) - (b.appointment_hour || 0))
            }));
    }, [appointments]);

    return (
        <div style={{ overflowY: 'auto', height: '100%', padding: '4px 16px 16px' }}>
            {grouped.map(({ dateKey, dateLabel, isToday, items }) => (
                <div key={dateKey}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                        padding: '14px 0 6px', marginBottom: 6,
                        borderBottom: '1px solid #F3F4F6'
                    }}>
                        <span style={{ color: isToday ? '#3B82F6' : '#9CA3AF' }}>{dateLabel}</span>
                        {isToday && <span style={{ fontSize: 10, background: '#3B82F6', color: '#fff', borderRadius: 8, padding: '1px 6px', textTransform: 'none', letterSpacing: 0 }}>Hôm nay</span>}
                        <span style={{ marginLeft: 'auto', fontWeight: 500 }}>{items.length} lịch hẹn</span>
                    </div>
                    {items.map((item) => (
                        <AppointmentCard key={item.id || item.code} item={item} onClick={onCardClick} />
                    ))}
                </div>
            ))}
            {grouped.length === 0 && (
                <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 14, paddingTop: 40 }}>
                    Không có lịch hẹn
                </div>
            )}
        </div>
    );
}

// ─── CalendarView ────────────────────────────────────────────────────────────
function CalendarView({ appointments, onCardClick, startDayHour, endDayHour }) {
    const data = useMemo(() => {
        return appointments
            .filter((a) => a.appointment_date)
            .map((a) => {
                try {
                    const startDate = AntTime.newDateAndTime(a.appointment_date, a.appointment_hour).toDate();
                    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // +1h default
                    return { ...a, startDate, endDate, text: a.entity_name || a.code || '' };
                } catch (e) {
                    return null;
                }
            })
            .filter(Boolean);
    }, [appointments]);

    const appointmentRender = useCallback(({ appointmentData: d }) => {
        const sc = getStatus(d.appointment_status);
        return (
            <div style={{ background: sc.color, height: '100%', padding: '2px 6px', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ color: '#fff', fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>{d.entity_name || '—'}</div>
                {d.entity_contact_phone ? <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10 }}>{d.entity_contact_phone}</div> : null}
                {d.serve_entity_name ? <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>◉ {d.serve_entity_name}</div> : null}
            </div>
        );
    }, []);

    const onDblClick = useCallback((e) => {
        e.cancel = true;
        if (e.appointmentData) onCardClick(e.appointmentData);
    }, [onCardClick]);

    const onCellClick = useCallback((e) => { e.cancel = true; }, []);

    return (
        <Scheduler
            dataSource={data}
            startDayHour={startDayHour || 7}
            endDayHour={endDayHour || 22}
            defaultCurrentView="week"
            startDateExpr="startDate"
            endDateExpr="endDate"
            textExpr="text"
            showAllDayPanel={false}
            appointmentRender={appointmentRender}
            onAppointmentDblClick={onDblClick}
            onCellClick={onCellClick}
            editing={false}
            height="100%"
        >
            <View type="day" name="Ngày" />
            <View type="week" name="Tuần" />
            <View type="month" name="Tháng" />
        </Scheduler>
    );
}

// ─── Shared styles ───────────────────────────────────────────────────────────
const navBtnStyle = {
    width: 28, height: 28, border: '1px solid #E5E7EB', borderRadius: 6,
    background: '#fff', cursor: 'pointer', fontSize: 16, color: '#374151',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1
};

const viewBtnBase = {
    padding: '5px 12px', border: 'none', borderRadius: 6, cursor: 'pointer',
    fontWeight: 500, fontSize: 13, transition: 'all 0.15s'
};

const VIEWS = [
    { key: 'list', label: { vi: 'Danh sách', en: 'List', fr: 'Liste', es: 'Lista', id: 'Daftar', ja: '一覧', zh: '列表', ko: '목록', hi: 'सूची', ar: 'القائمة' } },
    { key: 'kanban', label: { vi: 'Kanban', en: 'Kanban', fr: 'Kanban', es: 'Kanban', id: 'Kanban', ja: 'カンバン', zh: '看板', ko: '칸반', hi: 'कानबन', ar: 'كانبان' } },
    { key: 'calendar', label: { vi: 'Lịch', en: 'Calendar', fr: 'Calendrier', es: 'Calendario', id: 'Kalender', ja: 'カレンダー', zh: '日历', ko: '캘린더', hi: 'कैलेंडर', ar: 'التقويم' } }
];

// ─── Main component ──────────────────────────────────────────────────────────
/**
 * AntAppointmentView - Màn hình quản lý lịch hẹn với 3 chế độ xem
 *
 * Props:
 *   API           {AntAPI}    - API instance cho ARAppointment
 *   session       {object}    - Session object từ parent
 *   navigate      {function}  - Hàm điều hướng: (url) => void
 *   startDayHour  {number}    - Giờ bắt đầu ngày làm việc (default: 7)
 *   endDayHour    {number}    - Giờ kết thúc ngày làm việc (default: 22)
 *   searchConditions {object} - Điều kiện tìm kiếm bổ sung (store_code, tags,...)
 *   onCreateClick {function}  - Override hành động tạo lịch hẹn mới
 */
export default function AntAppointmentView(props) {
    const [view, setView] = useState('kanban');
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterDate, setFilterDate] = useState(AntTime.now().format('YYYY-MM-DD'));

    const loadData = useCallback(async () => {
        if (!props.API) return;
        setLoading(true);
        try {
            const params = { pageSize: 500, total: false, ...(props.searchConditions || {}) };
            const result = await props.API.getList(params);
            setAppointments(result.rows || []);
        } catch (e) {
            console.error('AntAppointmentView.loadData error:', e);
        }
        setLoading(false);
    }, [props.API, props.searchConditions]);

    useEffect(() => { loadData(); }, [loadData]);

    const stats = useMemo(() => {
        const c = {};
        STATUS_CONFIG.forEach((s) => { c[s.key] = 0; });
        appointments.forEach((a) => { if (c[a.appointment_status] !== undefined) c[a.appointment_status]++; });
        return c;
    }, [appointments]);

    const handleCardClick = useCallback((item) => {
        if (props.navigate) props.navigate(`/ar/ARAppointment/${item.code}`);
    }, [props.navigate]);

    const handleCreate = useCallback(() => {
        if (props.onCreateClick) props.onCreateClick();
        else if (props.navigate) props.navigate('/ar/ARAppointment/');
    }, [props.navigate, props.onCreateClick]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#F8FAFC' }}>

            {/* ── Toolbar ─────────────────────────────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px', background: '#fff',
                borderBottom: '1px solid #E5E7EB', flexShrink: 0, flexWrap: 'wrap'
            }}>
                {/* View switcher */}
                <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 3, gap: 2 }}>
                    {VIEWS.map((v) => (
                        <button key={v.key} onClick={() => setView(v.key)} style={{
                            ...viewBtnBase,
                            background: view === v.key ? '#fff' : 'transparent',
                            color: view === v.key ? '#1F2937' : '#6B7280',
                            boxShadow: view === v.key ? '0 1px 3px rgba(0,0,0,0.12)' : 'none'
                        }}>
                            {AntLocalization.translateText(v.label)}
                        </button>
                    ))}
                </div>

                <div style={{ flex: 1 }} />

                <button onClick={loadData} disabled={loading} style={{
                    padding: '6px 12px', border: '1px solid #E5E7EB', borderRadius: 6,
                    background: '#fff', cursor: loading ? 'wait' : 'pointer', fontSize: 13, color: '#374151'
                }}>
                    {loading ? '...' : '↺ Làm mới'}
                </button>

                <button onClick={handleCreate} style={{
                    padding: '6px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
                    background: '#3B82F6', color: '#fff', fontWeight: 600, fontSize: 13
                }}>
                    + Tạo lịch hẹn
                </button>
            </div>

            {/* ── Stats bar ────────────────────────────────────────────────── */}
            <div style={{
                display: 'flex', gap: 8, padding: '8px 16px', background: '#fff',
                borderBottom: '1px solid #E5E7EB', flexShrink: 0, flexWrap: 'wrap', alignItems: 'center'
            }}>
                <span style={{ fontSize: 12, color: '#6B7280', marginRight: 4 }}>
                    {appointments.length} lịch hẹn
                </span>
                {STATUS_CONFIG.map((sc) => (
                    <div key={sc.key} style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '3px 10px', borderRadius: 20, background: sc.bg, border: `1px solid ${sc.border}`
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.color, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: '#4B5563' }}>
                            {AntLocalization.translateText({ vi: sc.vi, en: sc.en })}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: sc.color }}>{stats[sc.key] || 0}</span>
                    </div>
                ))}
            </div>

            {/* ── Loading overlay ───────────────────────────────────────────── */}
            {loading && (
                <div style={{ padding: '8px 16px', background: '#EFF6FF', borderBottom: '1px solid #BFDBFE', flexShrink: 0 }}>
                    <span style={{ fontSize: 12, color: '#3B82F6' }}>Đang tải dữ liệu...</span>
                </div>
            )}

            {/* ── Content ──────────────────────────────────────────────────── */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
                {view === 'kanban' && (
                    <KanbanView
                        appointments={appointments}
                        onCardClick={handleCardClick}
                        filterDate={filterDate}
                        onFilterDateChange={setFilterDate}
                    />
                )}
                {view === 'list' && (
                    <ListView appointments={appointments} onCardClick={handleCardClick} />
                )}
                {view === 'calendar' && (
                    <CalendarView
                        appointments={appointments}
                        onCardClick={handleCardClick}
                        startDayHour={props.startDayHour}
                        endDayHour={props.endDayHour}
                    />
                )}
            </div>
        </div>
    );
}
